import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface TocItem {
  id: string;
  label: string;
}

interface TableOfContentsProps {
  items: TocItem[];
}

const TableOfContents = ({ items }: TableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');
  const itemRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState<{
    top: number;
    height: number;
  } | null>(null);

  const updateIndicator = useCallback((id: string) => {
    const el = itemRefs.current.get(id);
    if (el) {
      setIndicatorStyle({
        top: el.offsetTop,
        height: el.offsetHeight,
      });
    }
  }, []);

  // Track which section is in view
  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const topEntry = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top
              ? prev
              : curr,
          );
          const id = topEntry.target.id;
          setActiveId(id);
          updateIndicator(id);
        }
      },
      { rootMargin: '-10% 0px -80% 0px', threshold: 0 },
    );

    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) {
        observer.observe(el);
      }
    }

    return () => observer.disconnect();
  }, [items, updateIndicator]);

  // Update indicator position when active item changes
  useEffect(() => {
    updateIndicator(activeId);
  }, [activeId, updateIndicator]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className="yst-sticky yst-top-4 yst-overflow-visible"
    >
      <div className="yst-relative yst-flex yst-flex-col">
        {/* Sliding indicator */}
        {indicatorStyle && (
          <div
            className="yst-absolute yst-left-0 yst-w-0.5 yst-bg-slate-700 yst-rounded-full"
            style={{
              top: indicatorStyle.top,
              height: indicatorStyle.height,
              transition: 'top 0.3s ease, height 0.3s ease',
            }}
          />
        )}

        {/* TOC items */}
        {items.map((item) => (
          <button
            key={item.id}
            ref={(el) => {
              itemRefs.current.set(item.id, el);
            }}
            type="button"
            onClick={() => handleClick(item.id)}
            className={cx(
              'yst-text-left yst-pl-4 yst-py-1.5 yst-text-sm yst-transition-colors yst-duration-200',
              activeId === item.id
                ? 'yst-text-slate-700 yst-font-semibold'
                : 'yst-text-slate-400 hover:yst-text-slate-500',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>
    </nav>
  );
};

export default TableOfContents;
